import { useMutation } from '@apollo/client'
import {
  CustomizationFieldFragment,
  ExpensesCustomizationFieldsDocument,
  ExpensesValueObjectsCustomizationDocumentTypeEnum,
  FinanceTaxRateObjectInput,
  FinanceValueObjectsTaxTypeEnum,
  UpdateCustomizationFieldDocument,
  UpdateCustomizationFieldMutationVariables,
} from 'generated/resources/graphql'

import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

type ExpensesMutationsUpdateExpensesCustomizationFieldArgs =
  UpdateCustomizationFieldMutationVariables

export type CustomizationField = CustomizationFieldFragment

export interface OptionItem {
  parent_id?: string
  id?: string
  hidden?: boolean
  value: string | number
  label: string
}

export interface Tax {
  id?: string
  base_amount?: number | null
  percentage?: number | null
  amount?: number | null
  type?: FinanceValueObjectsTaxTypeEnum | null
  tax_type_id?: string | null
  tax_type_name?: string | null
  tax_rates?: FinanceTaxRateObjectInput[] | []
}

export const Identifiers = {
  category: 'category',
  subcategory: 'subcategory',
  merchant_name: 'merchant_name',
  merchant_tin: 'merchant_tin',
  currency: 'currency',
  amount: 'amount',
  effective_on: 'effective_on',
  description: 'description',
  files: 'files',
  budget_id: 'budget_id',
  units: 'units',
  mileage: 'mileage',
  rate: 'rate',
  from: 'from',
  to: 'to',
  start_date: 'start_date',
  end_date: 'end_date',
  document_number: 'document_number',
  document_type: 'document_type',
  payment: 'payment',
  payment_method: 'payment_method',
  exchange_rate: 'exchange_rate',
  reimbursable_currency: 'reimbursable_currency',
  reimbursable_amount: 'reimbursable_amount',
  taxes: 'taxes',
  projects: 'projects',
  per_diem_rates: 'rates',
  internal_reference: 'internal_reference',
  cost_centers: 'cost_centers',
}

export type Identifier = keyof typeof Identifiers

export const Resources = {
  expense: 'expense',
  mileage: 'mileage',
  perdiem: 'perdiem',
}

export const FieldTypes = {
  text: 'text',
  long_text: 'long_text',
  date: 'date',
  single_choice: 'single_choice',
  cents: 'cents',
  file: 'file',
  tax_type: 'tax_type',
}

export const TaxesLimit = 5

const identifierLabels = () => {
  const identifierLabelRecords: Record<string, Record<string, string>> = {
    [Resources.perdiem]: {
      [Identifiers.subcategory]: i18n.t('expenses_v2.forms.fields.subcategory.label'),
      [Identifiers.from]: i18n.t('expenses_v2.forms.fields.from.label'),
      [Identifiers.to]: i18n.t('expenses_v2.forms.fields.to.label'),
      [Identifiers.start_date]: i18n.t('expenses_v2.forms.fields.start_date.label'),
      [Identifiers.end_date]: i18n.t('expenses_v2.forms.fields.end_date.label'),
      [Identifiers.projects]: i18n.t('expenses_v2.forms.fields.projects.label'),
      [Identifiers.budget_id]: i18n.t('expenses_v2.forms.fields.budget.label'),
      [Identifiers.description]: i18n.t('expenses_v2.forms.fields.description.label'),
      [Identifiers.files]: i18n.t('expenses_v2.forms.fields.upload_files.label'),
      [Identifiers.per_diem_rates]: i18n.t('expenses_v2.forms.fields.per_diem_rates.label'),
      [Identifiers.internal_reference]: i18n.t('expenses_v2.forms.fields.reference.label'),
      [Identifiers.cost_centers]: i18n.t(
        'expenses_v2.forms.new_form.blocks.cost_center_distribution_info'
      ),
    },
    [Resources.expense]: {
      [Identifiers.category]: i18n.t('expenses_v2.forms.fields.category.label'),
      [Identifiers.subcategory]: i18n.t('expenses_v2.forms.fields.subcategory.label'),
      [Identifiers.merchant_name]: i18n.t('expenses_v2.forms.fields.vendor_name.label'),
      [Identifiers.merchant_tin]: i18n.t('expenses_v2.forms.fields.vendor_tin.label'),
      [Identifiers.currency]: i18n.t('expenses_v2.forms.fields.document_currency.label'),
      [Identifiers.amount]: i18n.t('expenses_v2.forms.fields.total_amount.label'),
      [Identifiers.effective_on]: i18n.t('expenses_v2.forms.fields.effective_on.label'),
      [Identifiers.description]: i18n.t('expenses_v2.forms.fields.description.label'),
      [Identifiers.internal_reference]: i18n.t('expenses_v2.forms.fields.reference.label'),
      [Identifiers.files]: i18n.t('expenses_v2.forms.fields.upload_files.label_mileage'),
      [Identifiers.budget_id]: i18n.t('expenses_v2.forms.fields.budget.label'),
      [Identifiers.document_number]: i18n.t('expenses_v2.forms.fields.document_number.label'),
      [Identifiers.document_type]: i18n.t('expenses_v2.forms.fields.document_type.label'),
      [Identifiers.cost_centers]: i18n.t('expenses_v2.forms.fields.cost_centers.label'),
      [Identifiers.payment]: i18n.t('expenses_v2.forms.fields.payment.label'),
      [Identifiers.payment_method]: i18n.t('expenses_v2.forms.fields.payment_method.label'),
      [Identifiers.exchange_rate]: i18n.t('expenses_v2.forms.fields.exchange_rate.label'),
      [Identifiers.reimbursable_currency]: i18n.t(
        'expenses_v2.forms.fields.reimbursable_currency.label'
      ),
      [Identifiers.reimbursable_amount]: i18n.t(
        'expenses_v2.forms.fields.reimbursable_amount.label'
      ),
      [Identifiers.taxes]: i18n.t('expenses_v2.forms.fields.taxes.label'),
      [Identifiers.projects]: i18n.t('expenses_v2.forms.fields.projects.label'),
      [Identifiers.cost_centers]: i18n.t(
        'expenses_v2.forms.new_form.blocks.cost_center_distribution_info'
      ),
    },
    [Resources.mileage]: {
      [Identifiers.subcategory]: i18n.t('expenses_v2.forms.fields.subcategory.label'),
      [Identifiers.units]: i18n.t('expenses_v2.forms.fields.units.label'),
      [Identifiers.mileage]: i18n.t('expenses_v2.forms.fields.mileage.label'),
      [Identifiers.currency]: i18n.t('expenses_v2.forms.fields.currency.label'),
      [Identifiers.rate]: i18n.t('expenses_v2.forms.fields.rate.label'),
      [Identifiers.amount]: i18n.t('expenses_v2.forms.fields.total_amount_mileage.label'),
      [Identifiers.effective_on]: i18n.t('expenses_v2.forms.fields.mileage_effective_on.label'),
      [Identifiers.from]: i18n.t('expenses_v2.forms.fields.from.label'),
      [Identifiers.to]: i18n.t('expenses_v2.forms.fields.to.label'),
      [Identifiers.description]: i18n.t('expenses_v2.forms.fields.description.label'),
      [Identifiers.files]: i18n.t('expenses_v2.forms.fields.upload_files.label_mileage'),
      [Identifiers.budget_id]: i18n.t('expenses_v2.forms.fields.budget.label'),
      [Identifiers.cost_centers]: i18n.t(
        'expenses_v2.forms.new_form.blocks.cost_center_distribution_info'
      ),
      [Identifiers.projects]: i18n.t('expenses_v2.forms.fields.projects.label'),
      [Identifiers.internal_reference]: i18n.t('expenses_v2.forms.fields.reference.label'),
    },
  }
  return identifierLabelRecords
}

const descriptions = () => {
  const descriptionsRecords: Record<string, Record<string, string>> = {
    [Resources.expense]: {
      [Identifiers.category]: i18n.t('expenses_v2.forms.fields.category.description'),
      [Identifiers.subcategory]: i18n.t('expenses_v2.forms.fields.subcategory.description'),
      [Identifiers.merchant_name]: i18n.t('expenses_v2.forms.fields.vendor_name.description'),
      [Identifiers.merchant_tin]: i18n.t('expenses_v2.forms.fields.vendor_tin.description'),
      [Identifiers.currency]: i18n.t('expenses_v2.forms.fields.document_currency.description'),
      [Identifiers.amount]: i18n.t('expenses_v2.forms.fields.total_amount.description'),
      [Identifiers.effective_on]: i18n.t('expenses_v2.forms.fields.effective_on.description'),
      [Identifiers.description]: i18n.t('expenses_v2.forms.fields.description.description'),
      [Identifiers.internal_reference]: i18n.t('expenses_v2.forms.fields.reference.description'),
      [Identifiers.files]: i18n.t('expenses_v2.forms.fields.upload_files.description'),
      [Identifiers.budget_id]: i18n.t('expenses_v2.forms.fields.budget.description'),
      [Identifiers.document_number]: i18n.t('expenses_v2.forms.fields.document_number.description'),
      [Identifiers.document_type]: i18n.t('expenses_v2.forms.fields.document_type.description'),
      [Identifiers.cost_centers]: i18n.t('expenses_v2.forms.fields.cost_centers.description'),
      [Identifiers.payment]: i18n.t('expenses_v2.forms.fields.payment.description'),
      [Identifiers.payment_method]: i18n.t('expenses_v2.forms.fields.payment_method.description'),
      [Identifiers.exchange_rate]: i18n.t('expenses_v2.forms.fields.exchange_rate.description'),
      [Identifiers.reimbursable_currency]: i18n.t(
        'expenses_v2.forms.fields.reimbursable_currency.description'
      ),
      [Identifiers.reimbursable_amount]: i18n.t(
        'expenses_v2.forms.fields.reimbursable_amount.description'
      ),
      [Identifiers.taxes]: i18n.t('expenses_v2.forms.fields.taxes.description'),
      [Identifiers.projects]: i18n.t('expenses_v2.forms.fields.projects.description'),
      [Identifiers.cost_centers]: i18n.t(
        'expenses_v2.forms.new_form.blocks.cost_center_distribution_info_subtitle'
      ),
    },
    [Resources.mileage]: {
      [Identifiers.subcategory]: i18n.t('expenses_v2.forms.fields.subcategory.description'),
      [Identifiers.units]: i18n.t('expenses_v2.forms.fields.units.description'),
      [Identifiers.mileage]: i18n.t('expenses_v2.forms.fields.mileage.description'),
      [Identifiers.currency]: i18n.t('expenses_v2.forms.fields.currency.description'),
      [Identifiers.rate]: i18n.t('expenses_v2.forms.fields.rate.description'),
      [Identifiers.amount]: i18n.t('expenses_v2.forms.fields.total_amount_mileage.description'),
      [Identifiers.effective_on]: i18n.t(
        'expenses_v2.forms.fields.mileage_effective_on.description'
      ),
      [Identifiers.from]: i18n.t('expenses_v2.forms.fields.from.description'),
      [Identifiers.to]: i18n.t('expenses_v2.forms.fields.to.description'),
      [Identifiers.description]: i18n.t('expenses_v2.forms.fields.description.description'),
      [Identifiers.files]: i18n.t('expenses_v2.forms.fields.upload_files.description'),
      [Identifiers.cost_centers]: i18n.t(
        'expenses_v2.forms.new_form.blocks.cost_center_distribution_info_subtitle'
      ),
      [Identifiers.projects]: i18n.t('expenses_v2.forms.fields.projects.description'),
      [Identifiers.internal_reference]: i18n.t('expenses_v2.forms.fields.reference.description'),
    },
    [Resources.perdiem]: {
      [Identifiers.subcategory]: i18n.t('expenses_v2.forms.fields.subcategory.description'),
      [Identifiers.from]: i18n.t('expenses_v2.forms.fields.from.description'),
      [Identifiers.to]: i18n.t('expenses_v2.forms.fields.to.description'),
      [Identifiers.start_date]: i18n.t('expenses_v2.forms.fields.start_date.description'),
      [Identifiers.end_date]: i18n.t('expenses_v2.forms.fields.end_date.description'),
      [Identifiers.projects]: i18n.t('expenses_v2.forms.fields.projects.description'),
      [Identifiers.budget_id]: i18n.t('expenses_v2.forms.fields.budget.description'),
      [Identifiers.description]: i18n.t('expenses_v2.forms.fields.description.description'),
      [Identifiers.files]: i18n.t('expenses_v2.forms.fields.upload_files.description'),
      [Identifiers.internal_reference]: i18n.t('expenses_v2.forms.fields.reference.description'),
      [Identifiers.cost_centers]: i18n.t(
        'expenses_v2.forms.new_form.blocks.cost_center_distribution_info_subtitle'
      ),
    },
  }
  return descriptionsRecords
}

const typeLabels = () => {
  const typeLabelsRecords: Record<string, string> = {
    [FieldTypes.text]: i18n.t('expenses_v2.settings.forms.fields_customization.types.text'),
    [FieldTypes.long_text]: i18n.t(
      'expenses_v2.settings.forms.fields_customization.types.long_text'
    ),
    [FieldTypes.date]: i18n.t('expenses_v2.settings.forms.fields_customization.types.date'),
    [FieldTypes.cents]: i18n.t('expenses_v2.settings.forms.fields_customization.types.cents'),
    [FieldTypes.single_choice]: i18n.t(
      'expenses_v2.settings.forms.fields_customization.types.single_choice'
    ),
    [FieldTypes.file]: i18n.t('expenses_v2.settings.forms.fields_customization.types.file'),
    [FieldTypes.tax_type]: i18n.t('expenses_v2.settings.forms.fields_customization.types.cents'),
  }
  return typeLabelsRecords
}

export const findField = (
  customizationFields: CustomizationField[],
  id: string
): CustomizationField => {
  const foundCF = customizationFields.find((field) => field.id === id)
  if (!foundCF) throw new Error('Customization field not found')
  return foundCF
}

export const findFieldBy = (
  customizationFields: CustomizationField[],
  resourceType: keyof typeof Resources,
  identifier: keyof typeof Identifiers,
  documentType?: ExpensesValueObjectsCustomizationDocumentTypeEnum
) =>
  customizationFields.find(
    (field) =>
      field.resourceType == resourceType &&
      field.identifier == identifier &&
      (!documentType || field.documentType == documentType)
  )

export const fieldEnabled = (
  fields: CustomizationField[],
  resourceType: keyof typeof Resources,
  identifier: keyof typeof Identifiers
) => {
  const field = findFieldBy(fields, resourceType, identifier)
  return field?.enabled ?? false
}

export const fieldType = (
  fields: CustomizationField[],
  resourceType: keyof typeof Resources,
  identifier: keyof typeof Identifiers
  // eslint-disable-next-line no-type-assertion/no-type-assertion
) => findFieldBy(fields, resourceType, identifier)?.fieldType as keyof typeof FieldTypes | undefined

export type ResourceType = keyof typeof Resources
function isField(field: CustomizationField | ResourceType): field is CustomizationField {
  if (typeof field === 'string') return false
  return field.id !== undefined
}

export function fieldOptions(
  resourceType: ResourceType,
  identifier: keyof typeof Identifiers,
  fields: CustomizationField[]
): OptionItem[]
export function fieldOptions(field: CustomizationField): OptionItem[]
export function fieldOptions(
  resourceTypeOrField: ResourceType | CustomizationField,
  identifier?: keyof typeof Identifiers,
  fields?: CustomizationField[]
): OptionItem[] {
  if (isField(resourceTypeOrField)) {
    return JSON.parse(resourceTypeOrField.options)
  } else if (resourceTypeOrField && identifier && fields) {
    const field = findFieldBy(fields, resourceTypeOrField, identifier)
    if (field) {
      return fieldOptions(field)
    } else {
      return []
    }
  } else {
    return []
  }
}

export const fieldsFor = (fields: CustomizationField[], resourceType: keyof typeof Resources) =>
  fields?.filter((field) => field.resourceType === resourceType) || []

const mandatoryFields = (fields: CustomizationField[], resourceType: keyof typeof Resources) =>
  fieldsFor(fields, resourceType)
    .filter((field) => {
      return field.mandatory && field.enabled
    })
    .map((field) => field.identifier)
export const fieldLabel = (field: CustomizationField) => {
  return (identifierLabels()[field.resourceType] || {})[field.identifier] || ''
}

export const fieldDescription = (field: CustomizationField) => {
  return (descriptions()[field.resourceType] || {})[field.identifier] || ''
}

export const isMandatory = (
  field: string,
  resourceType: keyof typeof Resources,
  fields: CustomizationField[]
): boolean => {
  return mandatoryFields(fields, resourceType).includes(field)
}

export const typeLabel = (field: CustomizationField) => {
  return typeLabels()[field.fieldType] || ''
}

export const fieldTypes = (): OptionItem[] => {
  return Object.keys(typeLabels()).map((key) => {
    return {
      value: key,
      label: typeLabels()[key] || '',
    }
  })
}

export const isCurrency = (field: CustomizationField) => field.identifier === Identifiers.currency

export const isMileageRate = (field: CustomizationField) => field.identifier === Identifiers.rate

export const isCategory = (field: CustomizationField) => field.identifier === Identifiers.category

export const isPerDiemRates = (field: CustomizationField) =>
  field.identifier === Identifiers.per_diem_rates

export const isSubcategory = (field: CustomizationField) =>
  field.identifier === Identifiers.subcategory

export const isPaymentMethod = (field: CustomizationField) =>
  field.identifier === Identifiers.payment_method
export const isExchangeRate = (field: CustomizationField) =>
  field.identifier === Identifiers.exchange_rate

export const isProjects = (field: CustomizationField) => field.identifier === Identifiers.projects
export const isReimbursableFieldEnabled = (
  customizationFields: CustomizationField[],
  payment: string | null | undefined,
  identifier: keyof typeof Identifiers
) => fieldEnabled(customizationFields, 'expense', identifier) && payment === 'reimbursable'

export const mileageRateOptions = (
  fields: CustomizationField | CustomizationField[]
): OptionItem[] => {
  if (Array.isArray(fields)) {
    const field = findFieldBy(fields, 'mileage', 'rate')
    return field ? mileageRateOptions(field) : []
  } else if (isMileageRate(fields)) {
    return fieldOptions(fields).map((option) => {
      return { label: option.label, value: Number(option.value) }
    })
  } else {
    return []
  }
}
export const subcategoriesForCategory = (
  fields: CustomizationField[],
  resourceType: keyof typeof Resources,
  category: string,
  options?: { includeHidden: boolean }
) =>
  fieldOptions(resourceType, 'subcategory', fields)
    .filter(
      ({ parent_id, hidden }) => parent_id === category && (options?.includeHidden || !hidden)
    )
    .map((option) => ({ value: option.id, label: option.label }))
export const useExpensesCustomization = () => {
  const { data, error } = useQuery(ExpensesCustomizationFieldsDocument, {})
  return {
    fields: data?.expenses?.customizationFields,
    loading: !data,
    error,
  }
}

export const useExpensesCustomizationUpdate = () => {
  const [updateCustomizationField] = useMutation(UpdateCustomizationFieldDocument)
  const updateField = useUnsafeCallback(
    async ({
      companyId,
      id,
      enabled,
      mandatory,
      options,
    }: ExpensesMutationsUpdateExpensesCustomizationFieldArgs) => {
      return await updateCustomizationField({
        variables: {
          companyId,
          id,
          enabled,
          mandatory,
          options,
        },
        refetchQueries: [{ query: ExpensesCustomizationFieldsDocument, variables: {} }],
      })
    },
    [updateCustomizationField]
  )

  return { updateField }
}
